/** Admin → Location Service → Support: customer tickets (missing / damaged / wrong items, delivery, refunds…) with
 * replies and status, and the store's own FAQ shown on the customer Help page. */
import { useEffect, useState } from "react";
import { Pencil, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import { TICKET_CATEGORIES, statusLabel } from "../../../components/lse/LseSupport";
import { Btn, Empty, ErrorBox, Field, Loading, Modal, PageHead, Pill, Toggle, errText, inputCls } from "./LseUI";

const CAT = Object.fromEntries(TICKET_CATEGORIES);
const TONE = { OPEN: "amber", IN_PROGRESS: "blue", RESOLVED: "green", CLOSED: "muted" };

export default function LseSupportAdmin() {
    const [tab, setTab] = useState("tickets");
    return (
        <div data-testid="lse-support-admin">
            <PageHead title="Support" sub="Tickets your customers raise from the Help page, and the questions answered there." />
            <div className="mt-3 flex gap-2">
                {[["tickets", "Tickets"], ["faq", "FAQ"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setTab(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${tab === k ? "bg-foreground text-background border-foreground" : "border-border"}`}>{l}</button>
                ))}
            </div>
            {tab === "tickets" ? <Tickets /> : <Faqs />}
        </div>
    );
}

function Tickets() {
    const [status, setStatus] = useState("OPEN");
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState(null);
    const [open, setOpen] = useState(null);
    const load = () => { setErr(null); api.get(`/admin/lse/tickets${status ? `?status=${status}` : ""}`).then(({ data }) => setRows(data.tickets || [])).catch((e) => setErr(errText(e))); };
    useEffect(load, [status]);
    return (
        <>
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
                {["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED", ""].map((s) => (
                    <button key={s || "all"} type="button" onClick={() => setStatus(s)} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${status === s ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{s ? statusLabel(s) : "all"}</button>
                ))}
            </div>
            {err && <ErrorBox message={err} onRetry={load} />}
            {!rows && !err && <Loading />}
            {rows?.length === 0 && <Empty>No tickets here.</Empty>}
            <div className="mt-3 bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden">
                {(rows || []).map((t) => (
                    <button key={t.id} type="button" onClick={() => setOpen(t.id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/40" data-testid={`lse-admin-ticket-${t.id}`}>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{t.subject}</p>
                            <p className="text-xs text-muted-foreground truncate">{CAT[t.category] || t.category} · {t.customer_name || t.customer_mobile}{t.order_code ? ` · ${t.order_code}` : ""} · {new Date(t.updated_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</p>
                        </div>
                        <Pill tone={TONE[t.status]}>{statusLabel(t.status)}</Pill>
                    </button>
                ))}
            </div>
            {open && <TicketModal id={open} onClose={() => { setOpen(null); load(); }} />}
        </>
    );
}

function TicketModal({ id, onClose }) {
    const [t, setT] = useState(null);
    const [msg, setMsg] = useState("");
    const load = () => api.get(`/admin/lse/tickets/${id}`).then(({ data }) => setT(data)).catch((e) => toast.error(errText(e)));
    useEffect(() => { load(); }, [id]);   // eslint-disable-line react-hooks/exhaustive-deps
    const reply = async () => {
        try { const { data } = await api.post(`/admin/lse/tickets/${id}/reply`, { message: msg }); setT(data); setMsg(""); } catch (e) { toast.error(errText(e)); }
    };
    const setStatus = async (status) => { try { const { data } = await api.patch(`/admin/lse/tickets/${id}`, { status }); setT(data); } catch (e) { toast.error(errText(e)); } };
    return (
        <Modal wide title={t ? t.subject : "Ticket"} onClose={onClose}
               footer={t && t.status !== "CLOSED" && (
                   <div className="flex gap-2 w-full">
                       <input className={`${inputCls} flex-1`} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Reply to the customer…" maxLength={2000} data-testid="lse-admin-ticket-reply" />
                       <Btn onClick={reply} disabled={!msg.trim()} aria-label="Send"><Send size={16} /></Btn>
                   </div>
               )}>
            {!t ? <Loading /> : (
                <>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                        <span>{CAT[t.category]}</span><span>·</span><span>{t.customer_name} {t.customer_mobile}</span>
                        {t.order_id && <><span>·</span><Link className="text-primary font-semibold" to={`/admin/orders/${t.order_id}`}>Order {t.order_code}</Link></>}
                        <span className="ml-auto inline-flex items-center gap-2">
                            <select className="rounded-lg bg-muted px-2 py-1 text-xs" value={t.status} onChange={(e) => setStatus(e.target.value)} aria-label="Ticket status">
                                {["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"].map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                            </select>
                        </span>
                    </div>
                    <ol className="mt-3 space-y-2">
                        {t.messages.map((m) => (
                            <li key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.from === "STAFF" ? "ml-auto bg-primary text-primary-foreground" : "bg-muted"}`}>
                                <p className="whitespace-pre-wrap break-words">{m.message}</p>
                                <p className="text-[10px] mt-1 opacity-75">{m.from === "STAFF" ? "Store" : "Customer"} · {new Date(m.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</p>
                            </li>
                        ))}
                    </ol>
                </>
            )}
        </Modal>
    );
}

function Faqs() {
    const [rows, setRows] = useState(null);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/admin/lse/faqs").then(({ data }) => setRows(data.faqs || [])).catch(() => setRows([]));
    useEffect(() => { load(); }, []);
    const save = async () => {
        const body = { question: edit.question, answer: edit.answer, category: edit.category || null, sort_order: Number(edit.sort_order || 0), active: !!edit.active };
        try { if (edit.id) await api.patch(`/admin/lse/faqs/${edit.id}`, body); else await api.post("/admin/lse/faqs", body); setEdit(null); load(); }
        catch (e) { toast.error(errText(e)); }
    };
    const del = async (f) => { if (!window.confirm("Delete this question?")) return; await api.delete(`/admin/lse/faqs/${f.id}`).catch(() => {}); load(); };
    return (
        <>
            <div className="mt-3 flex justify-between items-center gap-2">
                <p className="text-xs text-muted-foreground">When you add questions here they replace the built-in ones on the customer Help page.</p>
                <Btn onClick={() => setEdit({ question: "", answer: "", sort_order: (rows?.length || 0) * 10, active: true })} data-testid="lse-faq-add"><Plus size={16} /> Question</Btn>
            </div>
            {!rows && <Loading />}
            {rows?.length === 0 && <Empty>No questions yet — customers see the built-in FAQ.</Empty>}
            <div className="mt-3 space-y-2">
                {(rows || []).map((f) => (
                    <div key={f.id} className="bg-card border border-border rounded-2xl p-4 flex gap-3">
                        <div className="flex-1 min-w-0"><p className="font-semibold text-sm">{f.question} {!f.active && <Pill>hidden</Pill>}</p><p className="text-xs text-muted-foreground mt-1 line-clamp-3">{f.answer}</p></div>
                        <span className="inline-flex gap-2 shrink-0"><Btn tone="ghost" className="!px-3" onClick={() => setEdit(f)} aria-label="Edit"><Pencil size={15} /></Btn><Btn tone="ghost" className="!px-3" onClick={() => del(f)} aria-label="Delete"><Trash2 size={15} /></Btn></span>
                    </div>
                ))}
            </div>
            {edit && (
                <Modal title={edit.id ? "Edit question" : "New question"} onClose={() => setEdit(null)} footer={<><Btn tone="ghost" onClick={() => setEdit(null)}>Cancel</Btn><Btn onClick={save} disabled={edit.question.length < 3 || !edit.answer} data-testid="lse-faq-save">Save</Btn></>}>
                    <div className="grid gap-3">
                        <Field label="Question"><input className={inputCls} value={edit.question} maxLength={200} onChange={(e) => setEdit({ ...edit, question: e.target.value })} /></Field>
                        <Field label="Answer"><textarea className={inputCls} rows={5} value={edit.answer} maxLength={3000} onChange={(e) => setEdit({ ...edit, answer: e.target.value })} /></Field>
                        <Field label="Order (lower first)"><input className={inputCls} type="number" min="0" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} /></Field>
                        <Toggle checked={edit.active} onChange={(v) => setEdit({ ...edit, active: v })} label="Show to customers" />
                    </div>
                </Modal>
            )}
        </>
    );
}
